/**
 * TemplatesPage.tsx
 *
 * Unified email + SMS template management with card grid layout
 * and folder filtering. SMS editing navigates to the dedicated
 * SmsTemplateBuilderPage; email editing navigates to EmailTemplateBuilderPage.
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Mail,
  MessageSquare,
  LayoutGrid,
  Code,
  Copy,
  Blocks,
  Link as LinkIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Tabs } from '@/components/ui/Tabs';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui/Spinner';
import { FilterSelect } from '@/components/ui/FilterSelect';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import { usePageContext } from '@/hooks/usePageContext';
import * as marketingSvc from '@/services/marketing.service';
import type {
  EmailMarketingTemplate,
  SmsMarketingTemplate,
  TemplateSort,
} from '@/services/marketing.service';
import { getSmsSegments } from '@/components/sms-editor/sms-editor-constants';
import { TemplateThumbnail } from '@/components/marketing/TemplateThumbnail';
import EmailComponentsTab from './EmailComponentsTab';
import RequestInfoLinksTab from './RequestInfoLinksTab';
import { FolderSelect } from './FolderSelect';

/** Surface the API's own message (duplicate/reserved folder name, 404, …) if it sent one. */
function apiErrorMessage(err: unknown, fallback: string): string {
  const res = (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
    ?.response?.data;
  return res?.error?.message ?? res?.message ?? fallback;
}

// ─── Template name uniqueness ─────────────────────────────────────────────────
//
// v5 enforces one template name per account — across folders, case-insensitive —
// so a create that reuses a name comes back 422. The set below warns about that
// in the create form before the request goes out.
//
// Best-effort by design: it only covers the templates the page has actually
// loaded, so while a folder filter is hiding rows the server stays the real gate.
// Every mutation here surfaces its message rather than failing silently. Copies
// don't consult it at all — the duplicate endpoints name themselves, checking the
// whole account instead of one page.

const takenNameSet = (templates: { name: string }[]) =>
  new Set(templates.map((t) => (t.name ?? '').trim().toLowerCase()));

// ─── Sort + status filters ────────────────────────────────────────────────────
//
// Both are applied by the API, not here, so they hold across the whole account
// rather than reordering whichever page happens to be loaded. That matters: the
// list caps at 200 rows, and sorting only those would quietly lie about which
// template is the oldest.

const SORT_OPTIONS = [
  { label: 'Newest first', value: 'newest' },
  { label: 'Oldest first', value: 'oldest' },
  { label: 'Alphabetical', value: 'name' },
];

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: '1' },
  { label: 'Inactive', value: '0' },
];

interface TemplateListFiltersProps {
  sort: TemplateSort;
  onSortChange: (sort: TemplateSort) => void;
  /** '' = both; '1'/'0' map to the API's `active` flag. */
  status: string;
  onStatusChange: (status: string) => void;
}

function TemplateListFilters({
  sort,
  onSortChange,
  status,
  onStatusChange,
}: TemplateListFiltersProps) {
  return (
    <>
      <FilterSelect
        options={SORT_OPTIONS}
        value={sort}
        onChange={(v) => onSortChange(v as TemplateSort)}
        label="Sort templates"
        className="w-40"
      />
      <FilterSelect
        options={STATUS_OPTIONS}
        value={status}
        onChange={onStatusChange}
        label="Filter by status"
        className="w-36"
      />
    </>
  );
}

// ─── Template Card (shared between email and SMS) ─────────────────────────────

/**
 * Which editor opens this template. The API derives `isDnd` from the marker the
 * DnD compiler stamps on <body>, so the label costs nothing — this used to sniff
 * the compiled html, which meant a list of 200 cards pulled 200 email bodies just
 * to render 200 chips. A template with no content yet reads as HTML, which
 * matches what the builder does with an empty record.
 */
function EditorKindChip({ isDnd }: { isDnd: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold shrink-0',
        isDnd ? 'bg-orange-50 text-orange-700' : 'bg-slate-100 text-slate-600',
      )}
      title={
        isDnd
          ? 'Built with the drag & drop editor'
          : 'Built with the HTML editor'
      }
    >
      {isDnd ? <LayoutGrid className="h-3 w-3" /> : <Code className="h-3 w-3" />}
      {isDnd ? 'Drag & Drop' : 'HTML'}
    </span>
  );
}

interface TemplateCardProps {
  name: string;
  folderName: string;
  active: boolean;
  preview: React.ReactNode;
  onEdit: () => void;
  onDuplicate?: () => void;
  onDelete: () => void;
  meta?: React.ReactNode;
  /** Extra chip shown beside the folder chip (email tab: which editor authored it). */
  badge?: React.ReactNode;
}

function TemplateCard({
  name,
  folderName,
  active,
  preview,
  onEdit,
  onDuplicate,
  onDelete,
  meta,
  badge,
}: TemplateCardProps) {
  return (
    <div
      className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
      onClick={onEdit}
    >
      {/* Preview area */}
      <div className="h-44 bg-slate-50 overflow-hidden relative">{preview}</div>

      {/* Info */}
      <div className="p-4 border-t border-slate-100">
        <div className="font-semibold text-sm text-slate-900 truncate">{name}</div>
        {meta && <div className="text-xs text-slate-500 mt-1 truncate">{meta}</div>}
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="inline-block px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold capitalize truncate">
              {folderName || 'general'}
            </span>
            {badge}
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            <span
              className={cn(
                'w-2 h-2 rounded-full inline-block',
                active ? 'bg-green-500' : 'bg-red-400',
              )}
            />
            {active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Hover actions */}
      <div className="absolute top-2.5 right-2.5 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-primary hover:scale-110 transition-all"
          title="Edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        {onDuplicate && (
          <button
            className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-slate-700 hover:scale-110 transition-all"
            title="Duplicate"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          className="w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-slate-500 hover:text-red-500 hover:scale-110 transition-all"
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Email Templates Tab ─────────────────────────────────────────────────────

function EmailTemplatesTab() {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeFolder, setActiveFolder] = useState('');
  const [sort, setSort] = useState<TemplateSort>('newest');
  const [status, setStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', subject: '', description: '' });
  const [deleteTarget, setDeleteTarget] = useState<EmailMarketingTemplate | null>(null);

  // Templates
  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'email-templates', { search, folder: activeFolder, sort, status }],
    queryFn: () =>
      marketingSvc.listEmailTemplates({
        search,
        folder: activeFolder || undefined,
        sort,
        active: status === '' ? undefined : (Number(status) as 0 | 1),
        perPage: 200,
        // Metadata only. Bodies arrive per card via TemplateThumbnail — asking for
        // 200 of them inline exceeded the response limit and failed the request
        // outright, so accounts with large libraries saw an empty grid.
        withContent: 0,
      }),
  });

  // Folders
  const { data: foldersRaw } = useQuery({
    queryKey: ['marketing', 'email-template-folders'],
    queryFn: () => marketingSvc.getEmailTemplateFolders(),
  });

  const templates = data?.data ?? [];
  // Count from the loaded page so the selector stays in step with what's on screen;
  // fall back to the server's count when a folder filter is hiding the rest.
  const folders = useMemo(() => {
    if (!Array.isArray(foldersRaw)) return [];
    return foldersRaw.map((f) => ({
      id: f.id,
      name: f.name,
      count: activeFolder
        ? f.count
        : templates.filter((t) => (t.folder || 'general') === f.name).length,
    }));
  }, [foldersRaw, templates, activeFolder]);

  // Same rule for the "All Folders" row: while a filter hides templates, the only
  // honest total is the sum of the server's per-folder counts.
  const totalCount = useMemo(
    () => (activeFolder ? folders.reduce((sum, f) => sum + f.count, 0) : templates.length),
    [activeFolder, folders, templates],
  );

  // Search filter (client side)
  const filtered = useMemo(() => {
    if (!search || search.length < 2) return templates;
    const q = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.subject ?? '').toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  // Drives the empty state: an empty *filtered* list is a different message from
  // an account that has no templates at all.
  const isFiltered = !!search || !!activeFolder || status !== '';

  const takenNames = useMemo(() => takenNameSet(templates), [templates]);
  const nameCollision = takenNames.has(formData.name.trim().toLowerCase());

  const createMut = useMutation({
    // New templates land in whichever folder is being viewed; a duplicate keeps
    // the folder of the template it was copied from.
    mutationFn: (d: { name: string; subject?: string; description?: string; folder?: string }) =>
      marketingSvc.createEmailTemplate({ ...d, folder: d.folder ?? (activeFolder || 'general') }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'email-templates'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'email-template-folders'] });
      setCreateOpen(false);
      setFormData({ name: '', subject: '', description: '' });
      toast.success('Template created');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to create template')),
  });

  // A duplicate is cloned server-side from the stored template, so the copy keeps
  // the body, description, preheader and sender fields the card never loads. The
  // API also picks the "(Copy N)" name, which is why nothing is passed here: it
  // checks the whole account rather than the page's best-effort `takenNames`.
  // The copy always comes back inactive — the toast says so, because otherwise an
  // Inactive badge on a copy of a live template reads as a bug.
  const duplicateMut = useMutation({
    mutationFn: (id: number) => marketingSvc.duplicateEmailTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'email-templates'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'email-template-folders'] });
      toast.success('Template duplicated — the copy is inactive until you activate it');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to duplicate template')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => marketingSvc.deleteEmailTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'email-templates'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'email-template-folders'] });
      setDeleteTarget(null);
      toast.success('Deleted');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete template')),
  });

  // Navigate to builder page instead of inline builder
  const openBuilder = (t: EmailMarketingTemplate | null) => {
    const id = t?.emailTemplateId ?? 'new';
    navigate(`/marketing/templates/email/${id}`);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 flex-wrap px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FolderSelect
            scope="email"
            folders={folders}
            activeFolder={activeFolder}
            onSelect={setActiveFolder}
            totalCount={totalCount}
          />
          <TemplateListFilters
            sort={sort}
            onSortChange={setSort}
            status={status}
            onStatusChange={setStatus}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 w-52"
            />
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openBuilder(null)}>
            New Template
          </Button>
        </div>
      </div>

      {/* Card grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Mail className="h-16 w-16 text-slate-200 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No templates found</h3>
          <p className="text-slate-400 mb-4">
            {isFiltered
              ? 'Try a different search, folder or filter'
              : 'Create your first email template to get started'}
          </p>
          {!isFiltered && (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openBuilder(null)}>
              Create Template
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
          {filtered.map((t) => (
            <TemplateCard
              key={t.emailTemplateId}
              name={t.name}
              folderName={t.folder ?? 'general'}
              active={t.active}
              preview={
                <TemplateThumbnail
                  templateId={t.emailTemplateId}
                  hasContent={t.hasContent}
                  title={t.name}
                />
              }
              meta={t.subject ?? 'No subject'}
              badge={<EditorKindChip isDnd={t.isDnd} />}
              onEdit={() => openBuilder(t)}
              onDuplicate={() => duplicateMut.mutate(t.emailTemplateId)}
              onDelete={() => setDeleteTarget(t)}
            />
          ))}
        </div>
      )}
      </div>

      {/* Create template modal (quick create) */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Email Template"
        size="sm"
      >
        <div className="space-y-4">
          <FormField label="Template Name" required>
            <Input
              value={formData.name}
              onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
            />
            {nameCollision && (
              <p className="mt-1 text-sm text-red-600">
                “{formData.name.trim()}” is already used by another template in this account.
                Template names must be unique.
              </p>
            )}
          </FormField>
          <FormField label="Subject Line">
            <Input
              value={formData.subject}
              onChange={(e) => setFormData((f) => ({ ...f, subject: e.target.value }))}
            />
          </FormField>
          <FormField label="Description">
            <Textarea autoResize
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={createMut.isPending}
              disabled={!formData.name.trim() || nameCollision}
              onClick={() =>
                createMut.mutate({
                  name: formData.name.trim(),
                  subject: formData.subject,
                  description: formData.description,
                })
              }
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Template"
        message={`Delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => { if (deleteTarget) deleteMut.mutate(deleteTarget.emailTemplateId); }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ─── SMS Templates Tab ───────────────────────────────────────────────────────

function SmsTemplatesTab() {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeFolder, setActiveFolder] = useState('');
  const [sort, setSort] = useState<TemplateSort>('newest');
  const [status, setStatus] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<SmsMarketingTemplate | null>(null);

  // Templates
  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'sms-templates', { search, folder: activeFolder, sort, status }],
    queryFn: () =>
      marketingSvc.listSmsTemplates({
        search,
        folder: activeFolder || undefined,
        sort,
        active: status === '' ? undefined : (Number(status) as 0 | 1),
        perPage: 200,
      }),
  });

  // Folders
  const { data: foldersRaw } = useQuery({
    queryKey: ['marketing', 'sms-template-folders'],
    queryFn: () => marketingSvc.getSmsTemplateFolders(),
  });

  const templates = data?.data ?? [];
  const folders = useMemo(() => {
    if (!Array.isArray(foldersRaw)) return [];
    return foldersRaw.map((f) => ({
      id: f.id,
      name: f.name,
      count: activeFolder
        ? f.count
        : templates.filter((t) => (t.folder || 'general') === f.name).length,
    }));
  }, [foldersRaw, templates, activeFolder]);

  const totalCount = useMemo(
    () => (activeFolder ? folders.reduce((sum, f) => sum + f.count, 0) : templates.length),
    [activeFolder, folders, templates],
  );

  const filtered = useMemo(() => {
    if (!search || search.length < 2) return templates;
    const q = search.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        (t.messageBody ?? '').toLowerCase().includes(q),
    );
  }, [templates, search]);

  const isFiltered = !!search || !!activeFolder || status !== '';

  const deleteMut = useMutation({
    mutationFn: (id: number) => marketingSvc.deleteSmsTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-templates'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-template-folders'] });
      setDeleteTarget(null);
      toast.success('Deleted');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete template')),
  });

  // Cloned server-side from the stored row so description and mediaUrls travel
  // with the message body, and so the "(Copy N)" name is checked account-wide.
  // As with email, the copy always lands inactive.
  const duplicateMut = useMutation({
    mutationFn: (id: number) => marketingSvc.duplicateSmsTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-templates'] });
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-template-folders'] });
      toast.success('Template duplicated — the copy is inactive until you activate it');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to duplicate template')),
  });

  const openBuilder = (t: SmsMarketingTemplate | null) => {
    const tid = t?.smsTemplateId ?? 'new';
    navigate(`/marketing/templates/sms/${tid}`);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center justify-between gap-4 flex-wrap px-5 pt-5 pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <FolderSelect
            scope="sms"
            folders={folders}
            activeFolder={activeFolder}
            onSelect={setActiveFolder}
            totalCount={totalCount}
          />
          <TemplateListFilters
            sort={sort}
            onSortChange={setSort}
            status={status}
            onStatusChange={setStatus}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 w-52"
            />
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openBuilder(null)}>
            New Template
          </Button>
        </div>
      </div>

      {/* Card grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <MessageSquare className="h-16 w-16 text-slate-200 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No templates found</h3>
          <p className="text-slate-400 mb-4">
            {isFiltered
              ? 'Try a different search, folder or filter'
              : 'Create your first SMS template to get started'}
          </p>
          {!isFiltered && (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => openBuilder(null)}>
              Create Template
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
          {filtered.map((t) => {
            const segs = getSmsSegments(t.messageBody ?? '');
            return (
              <TemplateCard
                key={t.smsTemplateId}
                name={t.name}
                folderName={t.folder ?? 'general'}
                active={t.active}
                preview={
                  t.messageBody ? (
                    <div className="p-4 text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap break-words h-full overflow-hidden relative">
                      {t.messageBody}
                      <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                      No preview available
                    </div>
                  )
                }
                meta={
                  <span className="flex items-center gap-2">
                    <Badge
                      variant={segs <= 4 ? 'success' : segs <= 8 ? 'info' : 'warning'}
                    >
                      {segs} segment{segs !== 1 ? 's' : ''}
                    </Badge>
                    {t.description && (
                      <span className="truncate">{t.description}</span>
                    )}
                  </span>
                }
                onEdit={() => openBuilder(t)}
                onDuplicate={() => duplicateMut.mutate(t.smsTemplateId)}
                onDelete={() => setDeleteTarget(t)}
              />
            );
          })}
        </div>
      )}
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Template"
        message={`Delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => { if (deleteTarget) deleteMut.mutate(deleteTarget.smsTemplateId); }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

// Every tab a ?tab= can name. A tab missing here still opens from the tab bar,
// but a link (or a builder's back arrow) naming it lands on email instead.
const TABS = ['email', 'sms', 'components', 'links'] as const;
type TabId = (typeof TABS)[number];

/** The tab with no ?tab= param, so a plain /marketing/templates link opens on email. */
const DEFAULT_TAB: TabId = 'email';

function isTabId(value: string | null): value is TabId {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export default function TemplatesPage() {
  const updateAIContext = usePageContext('Templates');
  // The open tab lives in ?tab= so a builder can send people back to the tab they
  // came from — the SMS builder's back arrow returns to ?tab=sms, not to email.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const active: TabId = isTabId(requested) ? requested : DEFAULT_TAB;

  useEffect(() => {
    updateAIContext({
      dataSummary:
        'Email and SMS template management page, the reusable email component library, and request-info links (one-tap "request more info" pages for campaigns)',
    });
  }, [updateAIContext]);

  const handleTabChange = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_TAB) {
      next.delete('tab');
    } else {
      next.set('tab', id);
    }
    setSearchParams(next, { replace: true });
  };

  const tabs = [
    { id: 'email', label: 'Email Templates', icon: <Mail className="h-4 w-4" />, content: <EmailTemplatesTab /> },
    { id: 'sms', label: 'SMS Templates', icon: <MessageSquare className="h-4 w-4" />, content: <SmsTemplatesTab /> },
    { id: 'components', label: 'Email Components', icon: <Blocks className="h-4 w-4" />, content: <EmailComponentsTab /> },
    { id: 'links', label: 'Request-Info Links', icon: <LinkIcon className="h-4 w-4" />, content: <RequestInfoLinksTab /> },
  ];
  // Keyed on the tab so a URL change from outside the tab bar (the sidebar's plain
  // Templates link while on SMS) resets the uncontrolled Tabs to match.
  return (
    <Tabs
      key={active}
      tabs={tabs}
      defaultTab={active}
      onChange={handleTabChange}
      className="h-full overflow-hidden pt-2"
    />
  );
}
